import {
  isBinaryGrpcWebOrConnectContentType,
  isGrpcWebOrConnectContentType,
} from '@terra/shared-asset-processing';
import {
  BINARY_BODY_PREFIX,
  buildHarPostData,
  buildHarResponseContent,
  ensureBinarySafeGrpcHarRequest,
  ensureBinarySafeGrpcHarResponse,
  isGrpcWebVisibleBase64Payload,
  type HarGrpcBodyView,
  type StoredHarDisplayEntry,
} from '@terra/shared-utils';
import type { WebGrpcDecodedData } from '@terra/shared-schemas-tracker';
import type { HttpRequest } from '../types/http-request';
import {
  encodeBinaryBody,
  getContentTypeHeader,
  parseBinaryBody,
} from '../electron-shared/utils/binary-body';
import {
  buildStoredHarDisplayEntry,
  hasGrpcHarHistoryEntry,
  isGrpcHarEntry,
} from './harGrpcDisplay.utils';
import {
  resolveHarEntryRequestDisplayContent,
  resolveHarEntryResponseDisplayContent,
} from '@terra/shared-utils';

const DEFAULT_GRPC_MIME_TYPE = 'application/grpc-web+proto';

const looksLikeGrpcWebBinaryFrame = (body: string): boolean => {
  if (body.length < 5) {
    return false;
  }

  return Buffer.from(body, 'latin1')[0] === 0;
};

const normalizeProxyResponseBodyForHar = ({
  body,
  headers,
}: {
  body: string;
  headers: Record<string, string | string[]>;
}): string => {
  if (body.startsWith(BINARY_BODY_PREFIX)) {
    return body;
  }

  const { body: parsedBody, bodyEncoding } = parseBinaryBody(body);

  if (bodyEncoding === 'base64') {
    return body;
  }

  const contentType = getContentTypeHeader(headers);

  if (
    isBinaryGrpcWebOrConnectContentType(contentType) &&
    isGrpcWebVisibleBase64Payload(parsedBody)
  ) {
    return `${BINARY_BODY_PREFIX}${parsedBody}`;
  }

  if (
    isBinaryGrpcWebOrConnectContentType(contentType) ||
    looksLikeGrpcWebBinaryFrame(parsedBody)
  ) {
    return encodeBinaryBody(Buffer.from(parsedBody, 'latin1'));
  }

  return body;
};

const resolveGrpcMimeType = ({
  headers,
  fallback = DEFAULT_GRPC_MIME_TYPE,
}: {
  headers: Record<string, string | string[]>;
  fallback?: string;
}): string => getContentTypeHeader(headers) || fallback;

const headersRecordToHarHeaders = (
  headers: Record<string, string | string[]>,
): Array<{ name: string; value: string }> =>
  Object.entries(headers).flatMap(([name, value]) =>
    Array.isArray(value)
      ? value.map((entry) => ({ name, value: entry }))
      : [{ name, value }],
  );

const grpcMimeFromStoredBodyContentType = (
  value: string | undefined,
): string => (value && isGrpcWebOrConnectContentType(value) ? value : '');

const isProxyGrpcHarCandidate = ({
  request,
  requestHeaders,
  responseHeaders,
}: {
  request: HttpRequest;
  requestHeaders: Record<string, string | string[]>;
  responseHeaders: Record<string, string | string[]>;
}): boolean => {
  const requestMimeType =
    getContentTypeHeader(requestHeaders) ||
    grpcMimeFromStoredBodyContentType(request.bodyContentType);
  const responseMimeType =
    getContentTypeHeader(responseHeaders) ||
    grpcMimeFromStoredBodyContentType(request.responseBodyContentType);

  return isGrpcHarEntry({
    request: {
      method: request.method,
      url: request.url,
      headers: headersRecordToHarHeaders(requestHeaders),
      postData: requestMimeType ? { mimeType: requestMimeType } : undefined,
    },
    response:
      request.status != null
        ? {
            content: responseMimeType
              ? { mimeType: responseMimeType }
              : undefined,
            headers: headersRecordToHarHeaders(responseHeaders),
          }
        : undefined,
  });
};

export const buildProxyGrpcHarDisplayEntry = ({
  request,
  requestBody,
  responseBody,
  webGrpcDecodedData = null,
}: {
  request: HttpRequest;
  requestBody?: string;
  responseBody?: string;
  webGrpcDecodedData?: WebGrpcDecodedData | null;
}): StoredHarDisplayEntry | undefined => {
  const resolvedRequestBody = requestBody ?? request.body ?? '';
  const resolvedResponseBody = responseBody ?? request.responseBody ?? '';
  const requestHeaders = request.headers;
  const responseHeaders = request.responseHeaders ?? {};

  if (
    !isProxyGrpcHarCandidate({
      request,
      requestHeaders,
      responseHeaders,
    })
  ) {
    return undefined;
  }

  const { body: wireRequestBody, bodyEncoding: requestBodyEncoding } =
    parseBinaryBody(resolvedRequestBody);
  const requestMimeType = resolveGrpcMimeType({ headers: requestHeaders });
  const postData = buildHarPostData({
    body: wireRequestBody,
    bodyEncoding: requestBodyEncoding,
    headers: requestHeaders,
  }) ?? {
    mimeType: requestMimeType,
    text: '',
  };
  const normalizedResponseBody = normalizeProxyResponseBodyForHar({
    body: resolvedResponseBody,
    headers: responseHeaders,
  });
  const { body: wireResponseBody, bodyEncoding: responseBodyEncoding } =
    parseBinaryBody(normalizedResponseBody);
  const responseMimeType = resolveGrpcMimeType({
    headers: responseHeaders,
    fallback: requestMimeType,
  });
  const responseContent = buildHarResponseContent({
    body: wireResponseBody,
    bodyEncoding: responseBodyEncoding,
    headers: responseHeaders,
  }) ?? {
    mimeType: responseMimeType,
    text: '',
    size: 0,
  };

  return buildStoredHarDisplayEntry({
    request: ensureBinarySafeGrpcHarRequest({
      method: request.method,
      url: request.url,
      httpVersion: request.httpVersion,
      headers: headersRecordToHarHeaders(requestHeaders),
      postData,
    }),
    response:
      request.status != null
        ? ensureBinarySafeGrpcHarResponse({
            status: request.status,
            statusText: request.responseStatusMessage ?? '',
            httpVersion: request.responseHttpVersion ?? request.httpVersion,
            headers: headersRecordToHarHeaders(responseHeaders),
            content: responseContent,
          })
        : undefined,
    webGrpcDecodedData,
  });
};

export const hasProxyGrpcHarEntry = ({
  grpcHarDisplay,
}: {
  grpcHarDisplay?: StoredHarDisplayEntry;
}): boolean => hasGrpcHarHistoryEntry({ grpcHarDisplay });

export const resolveProxyRequestDisplayContent = ({
  grpcHarDisplay: harEntry,
  bodyView,
  fallbackContent,
}: {
  grpcHarDisplay?: StoredHarDisplayEntry;
  bodyView: HarGrpcBodyView;
  fallbackContent: string;
}): string =>
  resolveHarEntryRequestDisplayContent({
    harEntry,
    bodyView,
    fallbackContent,
  });

export const resolveProxyResponseDisplayContent = ({
  grpcHarDisplay: harEntry,
  bodyView,
  fallbackContent,
}: {
  grpcHarDisplay?: StoredHarDisplayEntry;
  bodyView: HarGrpcBodyView;
  fallbackContent: string;
}): string =>
  resolveHarEntryResponseDisplayContent({
    harEntry,
    bodyView,
    fallbackContent,
  });
